import { Link } from 'react-router-dom';
import type { AuditEntryInfo, OidcClientInfo } from '@shared/types';
import { formatRelative } from '@shared/format';
import { useAuthStore } from '../app/authStore';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Card, CardHeader, EmptyState, PageHeader, Skeleton, StatBlock } from '../components/ui';
import { Notice } from '../components/form';
import { Icon } from '../components/Icon';

interface OrgResponse {
  tenant: { id: string; name: string; slug: string; kind: string; createdAt: string };
  stats: {
    memberCount: number;
    pendingInvitationCount: number;
    clientCount: number;
    ownerEmail: string | null;
  };
}

/**
 * 概览。
 *
 * 这一页只回答两个问题：**「我现在安全吗」** 与 **「有什么等着我处理」**。
 * 因此第一屏是安全待办，而不是一堆总数。
 */
export function OverviewPage() {
  const session = useAuthStore((state) => state.session);
  const can = useAuthStore((state) => state.can);

  const org = useAsync<OrgResponse>(() => api.get('/api/org'), []);
  const audit = useAsync<{ items: AuditEntryInfo[] }>(
    () => (can('audit.view') ? api.get('/api/audit?pageSize=6') : Promise.resolve({ items: [] })),
    [can],
  );
  const clients = useAsync<{ clients: OidcClientInfo[] }>(
    () => (can('app.view') ? api.get('/api/applications') : Promise.resolve({ clients: [] })),
    [can],
  );

  const todos: { text: string; to: string; tone: 'warn' | 'info' }[] = [];
  if (session?.user.totpEnabled === false) {
    todos.push({ text: '尚未开启两步验证，建议立即绑定验证器', to: '/security', tone: 'warn' });
  }
  if ((org.data?.stats.pendingInvitationCount ?? 0) > 0) {
    todos.push({
      text: `有 ${org.data?.stats.pendingInvitationCount} 条邀请还未被接受`,
      to: '/members',
      tone: 'info',
    });
  }
  if (can('app.view') && (clients.data?.clients.length ?? 0) === 0) {
    todos.push({ text: '还没有接入应用；接入后可用 OIDC 单点登录', to: '/applications', tone: 'info' });
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={`你好，${session?.user.name ?? ''}`}
        description={`当前组织「${session?.tenant.name ?? ''}」，角色 ${
          { owner: '所有者', admin: '管理员', member: '成员', viewer: '观察员' }[session?.role ?? 'member']
        }`}
      />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-5">
          <Card>
            <CardHeader title="账号与组织" hint="组织维度的规模摘要" />
            {org.loading ? (
              <Skeleton className="h-20 w-full" />
            ) : (
              <div className="grid gap-3 sm:grid-cols-3">
                <StatBlock label="成员" value={org.data?.stats.memberCount ?? '—'} />
                <StatBlock label="接入应用" value={org.data?.stats.clientCount ?? '—'} />
                <StatBlock label="待接受邀请" value={org.data?.stats.pendingInvitationCount ?? '—'} />
              </div>
            )}
            <dl className="mt-4 space-y-2 text-[13px]">
              <Row label="组织标识" value={org.data?.tenant.slug ?? '—'} />
              <Row
                label="组织类型"
                value={org.data?.tenant.kind === 'personal' ? '个人' : '企业'}
              />
              <Row label="所有者" value={org.data?.stats.ownerEmail ?? '—'} />
              <Row label="创建时间" value={formatRelative(org.data?.tenant.createdAt)} />
            </dl>
          </Card>

          {can('audit.view') ? (
            <Card>
              <CardHeader
                title="最近操作"
                action={
                  <Link to="/audit" className="flex items-center gap-1 text-[13px] text-brand hover:underline">
                    查看全部
                    <Icon name="arrow-up-right" className="h-3.5 w-3.5" />
                  </Link>
                }
              />
              {audit.loading ? (
                <Skeleton className="h-24 w-full" />
              ) : (audit.data?.items.length ?? 0) === 0 ? (
                <EmptyState title="还没有操作记录" description="成员管理、接入配置等动作都会出现在这里。" />
              ) : (
                <ul className="divide-y divide-line">
                  {audit.data?.items.map((item) => (
                    <li key={item.id} className="flex items-center justify-between gap-3 py-2 text-[13px]">
                      <span className="min-w-0 flex-1 truncate text-fg">
                        <span className="font-medium">{item.actorEmail}</span>
                        <span className="text-fg-muted"> · {item.action}</span>
                      </span>
                      <span className="shrink-0 text-xs text-fg-subtle">{formatRelative(item.at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ) : null}
        </div>

        <div className="min-w-0 space-y-5">
          <Card>
            <CardHeader title="安全待办" hint="按风险从高到低排列" />
            {todos.length === 0 ? (
              <Notice tone="ok">没有待处理事项，账号处于推荐的安全配置下。</Notice>
            ) : (
              <ul className="space-y-2">
                {todos.map((todo) => (
                  <li key={todo.text}>
                    <Link
                      to={todo.to}
                      className="flex items-start gap-2 rounded-control bg-nest-2 px-3 py-2 text-[13px] text-fg hover:bg-nest"
                    >
                      <Icon
                        name={todo.tone === 'warn' ? 'alert' : 'help'}
                        className={`mt-0.5 h-4 w-4 shrink-0 ${todo.tone === 'warn' ? 'text-warn' : 'text-brand'}`}
                      />
                      <span className="flex-1">{todo.text}</span>
                      <Icon name="chevron-right" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-fg-subtle" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <CardHeader title="账号" />
            <p className="truncate text-[13px] font-medium text-fg">{session?.user.name}</p>
            <p className="truncate text-xs text-fg-muted">{session?.user.email}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              <Badge tone="brand">{session?.role === 'owner' ? '所有者' : session?.role}</Badge>
              {session?.user.totpEnabled ? (
                <Badge tone="ok">两步验证已开启</Badge>
              ) : (
                <Badge tone="warn">未开启两步验证</Badge>
              )}
            </div>
            <p className="mt-3 text-xs text-fg-muted">
              上次登录：{formatRelative(session?.user.lastLoginAt)}
            </p>
            <Link to="/security" className="mt-3 inline-flex text-[13px] text-brand hover:underline">
              前往安全设置
            </Link>
          </Card>

          <Card>
            <CardHeader title="机队" hint="机队数据在运营监管平台，控制台只做统一登录" />
            <p className="text-[13px] text-fg-muted">
              实时位置、遥测与指令下发都在运营监管平台完成；控制台负责账号与权限。
            </p>
            <Link
              to="/fleet"
              className="mt-3 inline-flex items-center gap-1 text-[13px] text-brand hover:underline"
            >
              查看机队入口
              <Icon name="arrow-up-right" className="h-3.5 w-3.5" />
            </Link>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-fg-muted">{label}</dt>
      <dd className="truncate text-fg">{value}</dd>
    </div>
  );
}
